import React, {useState, useEffect} from "react";
import {Table, Pagination, Form, Button, Card, Badge} from "react-bootstrap";
import clinicbookingService from "../../../../../services/clinicbooking.service";
import {useParams} from "react-router-dom";

function BookingRequests() {
    const [bookingRequests, setBookingRequests] = useState([]);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [totalElements, setTotalElements] = useState(0);
    const [expandedRows, setExpandedRows] = useState(new Set());
    const {clinicId} = useParams();

    useEffect(() => {
        getBookingRequests(currentPage, pageSize);
    }, [currentPage, pageSize]);

    const getBookingRequests = (pageNo, size) => {
        clinicbookingService
            .getBookingAppointmentRequests(clinicId, pageNo - 1, size)
            .then((res) => {
                setBookingRequests(res.data.content);
                setTotalPages(res.data.totalPages);
                setTotalElements(res.data.totalElements);
            })
            .catch((err) => {
                console.error("Error fetching booking requests", err);
            });
    };

    const toggleRowExpansion = (index) => {
        const newExpandedRows = new Set(expandedRows);
        if (newExpandedRows.has(index)) {
            newExpandedRows.delete(index);
        } else {
            newExpandedRows.add(index);
        }
        setExpandedRows(newExpandedRows);
    };

    const handlePageChange = (page) => {
        setCurrentPage(page);
    };

    const handlePageSizeChange = (e) => {
        setPageSize(parseInt(e.target.value));
        setCurrentPage(1);
    };

    const formatDate = (dateString) => {
        return dateString; // Already formatted from API
    };

    const getPatientTypeBadge = (isNewPatient) => {
        return isNewPatient ? (
            <Badge bg="success" className="ms-2">New Patient</Badge>
        ) : (
            <Badge bg="info" className="ms-2">Existing Patient</Badge>
        );
    };

    const formatPhoneNumber = (phone) => {
        // Basic phone formatting
        const cleaned = phone.replace(/\D/g, '');
        if (cleaned.length === 10) {
            return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
        }
        return phone;
    };

    const formatComments = (comments) => {
        if (!comments || comments.trim() === "") {
            return <span className="text-muted">No comments provided</span>;
        }
        return comments.split('\n').map((line, index) => (
            <span key={index}>
                {line}
                <br/>
            </span>
        ));
    };

    return (
        <div className="shadow-none card card-padding">
            <div className="card-body">
                <div className="d-flex justify-content-between align-items-center mb-3">
                    <div className="d-flex align-items-center small">
                        <span className="me-2">Show</span>
                        <Form.Select
                            size="sm"
                            style={{width: '80px'}}
                            value={pageSize}
                            onChange={handlePageSizeChange}
                        >
                            <option value="5">5</option>
                            <option value="10">10</option>
                            <option value="20">20</option>
                            <option value="50">50</option>
                        </Form.Select>
                        <span className="ms-2">entries</span>
                    </div>
                </div>

                <div className="table-responsive">
                    <table className="table table-striped table-bordered table-hover mb-0">
                        <thead>
                        <tr>
                            <th style={{width: '12%'}}>Doctor</th>
                            <th style={{width: '23%'}}>Patient</th>
                            <th style={{width: '12%'}}>Phone</th>
                            <th style={{width: '15%'}}>Email</th>
                            <th style={{width: '20%'}}>Created</th>
                            <th style={{width: '5%'}}>Status</th>
                            <th style={{width: '3%'}}>Actions</th>
                        </tr>
                        </thead>
                        <tbody>
                        {bookingRequests.length === 0 ? (
                            <tr>
                                <td colSpan="7" className="text-center py-4">
                                    No booking requests found.
                                </td>
                            </tr>
                        ) : (
                            bookingRequests.map((request, index) => (
                                <React.Fragment key={index}>
                                    {/* Main Row */}
                                    <tr
                                        className={expandedRows.has(index) ? "small table-active" : "small"}
                                        style={{cursor: 'pointer'}}
                                        onClick={() => toggleRowExpansion(index)}
                                    >
                                        <td>
                                            <div className="fw-semibold">{request.doctorName}</div>
                                        </td>
                                        <td>
                                            <div>
                                                {request.firstName} {request.lastName}
                                                {request.preferredName && ` (${request.preferredName})`}
                                                {getPatientTypeBadge(request.isNewPatient)}
                                            </div>
                                        </td>
                                        <td>{formatPhoneNumber(request.cellPhone)}</td>
                                        <td>
                                            <a href={`mailto:${request.email}`} onClick={(e) => e.stopPropagation()}>
                                                {request.email}
                                            </a>
                                        </td>
                                        <td>{formatDate(request.createdAt)}</td>
                                        <td>
                                            <Badge bg={request.status ? "danger" : "success"}>
                                                {request.status ? "QUEUED" : "PROCESSED"}
                                            </Badge>
                                        </td>
                                        <td onClick={(e) => e.stopPropagation()}>
                                            <Button
                                                variant="outline-primary"
                                                size="sm"
                                                onClick={() => toggleRowExpansion(index)}
                                                title={expandedRows.has(index) ? "Collapse" : "Expand"}
                                            >
                                                <i className={`bi ${expandedRows.has(index) ? "bi-chevron-up" : "bi-chevron-down"}`}></i>
                                            </Button>
                                        </td>
                                    </tr>

                                    {/* Expanded Details Row */}
                                    {expandedRows.has(index) && (
                                        <tr className="bg-light">
                                            <td colSpan="7">
                                                <div className="p-3">
                                                    <h6 className="mb-3 text-primary border-bottom pb-2">Appointment
                                                        Details</h6>

                                                    <div className="row">
                                                        {/* First Column - Preferences */}
                                                        <div className="col-lg-4 mb-3">
                                                            <div className="card h-100 card-border shadow-none">
                                                                <div className="card-body">
                                                                    <h6 className="card-title text-dark mb-3">
                                                                        <i className="bi bi-calendar-check me-2"></i>
                                                                        Time Preferences
                                                                    </h6>
                                                                    <div className="mb-3">
                                                                        <strong className="text-dark d-block mb-1">First
                                                                            Preference:</strong>
                                                                        <span className="text-success">
                                    <i className="bi bi-calendar me-1"></i>
                                                                            {request.firstPreferredDate}
                                </span>
                                                                        <br/>
                                                                        <span className="text-success">
                                    <i className="bi bi-clock me-1"></i>
                                                                            {request.firstPreferredTime}
                                </span>
                                                                    </div>
                                                                    <div>
                                                                        <strong className="text-dark d-block mb-1">Second
                                                                            Preference:</strong>
                                                                        <span className="text-success">
                                                                        <i className="bi bi-calendar me-1"></i>
                                                                                                                {request.secondPreferredDate}
                                                                    </span>
                                                                        <br/>
                                                                        <span className="text-success">
                                                                                <i className="bi bi-clock me-1"></i>
                                                                            {request.secondPreferredTime}
                                                                        </span>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </div>

                                                        {/* Second Column - Reason & Comments */}
                                                        <div className="col-lg-4 mb-3">
                                                            <div className="card h-100 card-border shadow-none">
                                                                <div className="card-body">
                                                                    <h6 className="card-title text-dark mb-3">
                                                                        <i className="bi bi-chat-dots me-2"></i>
                                                                        Visit Information
                                                                    </h6>
                                                                    <div className="mb-3">
                                                                        <strong className="text-dark d-block mb-1">Reason
                                                                            for Visit:</strong>
                                                                        <span className="text-info">
                                                                            <i className="bi bi-info-circle me-1"></i>
                                                                            {request.reason}
                                                                        </span>
                                                                    </div>
                                                                    <div>
                                                                        <strong
                                                                            className="text-dark d-block mb-1">Comments:</strong>
                                                                        <div className="bg-light p-2 rounded small">
                                                                            {formatComments(request.comments)}
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </div>

                                                        {/* Third Column - IDs and Patient Type */}
                                                        <div className="col-lg-4 mb-3">
                                                            <div className="card h-100 card-border shadow-none">
                                                                <div className="card-body">
                                                                    <h6 className="card-title text-dark mb-3">
                                                                        <i className="bi bi-person-badge me-2"></i>
                                                                        Identification
                                                                    </h6>

                                                                    {/* Clinic ID with Copy Button */}
                                                                    <div className="mb-3">
                                                                        <strong className="text-dark d-block mb-1">Clinic
                                                                            ID:</strong>
                                                                        <div className="d-flex align-items-center">
                                                                            <span
                                                                                className="text-muted small font-monospace me-2">
                                                                                {request.clinicId}
                                                                            </span>
                                                                            <Button
                                                                                variant="outline-secondary"
                                                                                size="sm"
                                                                                className="p-1"
                                                                                onClick={(e) => {
                                                                                    e.stopPropagation();
                                                                                    navigator.clipboard.writeText(request.clinicId);
                                                                                    // Show check icon temporarily
                                                                                    const button = e.target.closest('button');
                                                                                    const icon = button.querySelector('i');
                                                                                    icon.className = 'bi bi-check';
                                                                                    setTimeout(() => {
                                                                                        icon.className = 'bi bi-copy';
                                                                                    }, 2000);
                                                                                }}
                                                                                title="Copy Clinic ID"
                                                                            >
                                                                                <i className="bi bi-copy"></i>
                                                                            </Button>
                                                                        </div>
                                                                    </div>

                                                                    {/* Doctor ID with Copy Button */}
                                                                    <div className="mb-3">
                                                                        <strong className="text-dark d-block mb-1">Doctor
                                                                            ID:</strong>
                                                                        <div className="d-flex align-items-center">
                                                                            <span
                                                                                className="text-muted small font-monospace me-2">
                                                                                {request.bookingDoctorId}
                                                                            </span>
                                                                            <Button
                                                                                variant="outline-secondary"
                                                                                size="sm"
                                                                                className="p-1"
                                                                                onClick={(e) => {
                                                                                    e.stopPropagation();
                                                                                    navigator.clipboard.writeText(request.bookingDoctorId);
                                                                                    // Show check icon temporarily
                                                                                    const button = e.target.closest('button');
                                                                                    const icon = button.querySelector('i');
                                                                                    icon.className = 'bi bi-check';
                                                                                    setTimeout(() => {
                                                                                        icon.className = 'bi bi-copy';
                                                                                    }, 2000);
                                                                                }}
                                                                                title="Copy Doctor ID"
                                                                            >
                                                                                <i className="bi bi-copy"></i>
                                                                            </Button>
                                                                        </div>
                                                                    </div>

                                                                    {/* Patient Type */}
                                                                    <div>
                                                                        <strong className="text-dark d-block mb-1">Patient
                                                                            Type:</strong>
                                                                        <span
                                                                            className={`badge ${request.isNewPatient ? 'bg-success' : 'bg-info'}`}>
                                                                            <i className={`bi ${request.isNewPatient ? 'bi-person-plus' : 'bi-person-check'} me-1`}></i>
                                                                            {request.isNewPatient ? "New Patient" : "Existing Patient"}
                                                                        </span>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </td>
                                        </tr>
                                    )}
                                </React.Fragment>
                            ))
                        )}
                        </tbody>
                    </table>
                </div>

                <div className="d-flex justify-content-between align-items-center mt-3">
                    <div className="small text-secondary">
                        Showing {bookingRequests.length} of {totalElements} entries
                    </div>
                    <Pagination className="mb-0">
                        <Pagination.First
                            disabled={currentPage === 1}
                            onClick={() => handlePageChange(1)}
                        />
                        <Pagination.Prev
                            disabled={currentPage === 1}
                            onClick={() => handlePageChange(currentPage - 1)}
                        />

                        {[...Array(totalPages)].map((_, index) => {
                            const page = index + 1;
                            if (
                                page === 1 ||
                                page === totalPages ||
                                (page >= currentPage - 1 && page <= currentPage + 1)
                            ) {
                                return (
                                    <Pagination.Item
                                        key={page}
                                        active={page === currentPage}
                                        onClick={() => handlePageChange(page)}
                                    >
                                        {page}
                                    </Pagination.Item>
                                );
                            } else if (page === currentPage - 2 || page === currentPage + 2) {
                                return <Pagination.Ellipsis key={page}/>;
                            }
                            return null;
                        })}

                        <Pagination.Next
                            disabled={currentPage === totalPages}
                            onClick={() => handlePageChange(currentPage + 1)}
                        />
                        <Pagination.Last
                            disabled={currentPage === totalPages}
                            onClick={() => handlePageChange(totalPages)}
                        />
                    </Pagination>
                </div>
            </div>
        </div>
    );
}

export default BookingRequests;